import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Eye, FileSpreadsheet, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { listImportaciones, type Importacion } from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { inRange, parseIso } from '@/features/ventas/lib/dates';
import { setsEqual } from '@/features/tesoreria/components/FiltrarPopover';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import { FILTRO_INICIAL, FiltrarPopover, type FiltroImportaciones } from './FiltrarPopover';
import { ImportacionDialog } from './ImportacionDialog';
import {
  AnularImportacionDialog,
  EstadoImportacion,
  IconAction,
  fechaImp,
  montosPorMoneda,
} from './componentes';

const fob = (i: Importacion) => (i.totalFob != null ? formatMoney(i.totalFob, i.moneda) : 'Sin definir');

/** Etapa en curso del ciclo de vida; vacía si no tiene etapas. Sólo importa en las abiertas. */
const etapaEnCurso = (i: Importacion) =>
  i.status !== 'Abierta' || i.etapasTotal === 0 ? null : (i.etapaActual?.nombre ?? 'Completas');

/** Los montos van como número para que se puedan sumar en Excel. */
async function exportarXlsx(filas: Importacion[]): Promise<void> {
  await downloadXlsx({
    filename: `importaciones_${fechaArchivoHoy()}.xlsx`,
    sheetName: 'Importaciones',
    headers: [
      'N°',
      'Fecha',
      'Descripción',
      'Fabricante',
      'Fecha probable',
      'Etapa en curso',
      'Moneda',
      'Cant. FC',
      'Total FOB',
      'FOB instrumentado',
      'FOB por instrumentar',
      'Total USD',
      'Total ARS',
      'Pendiente USD',
      'Pendiente ARS',
      'Estado',
    ],
    rows: filas.map((i) => [
      i.numero,
      fechaImp(i.fecha),
      i.descripcion,
      i.fabricante ?? '',
      fechaImp(i.fechaProbable),
      etapaEnCurso(i) ?? '',
      i.moneda,
      i.cantidadFacturas,
      i.totalFob,
      i.fobInstrumentado,
      i.fobPorInstrumentar,
      i.total.USD ?? 0,
      i.total.ARS ?? 0,
      i.pendiente.USD ?? 0,
      i.pendiente.ARS ?? 0,
      i.status,
    ]),
  });
}

/** Listado de importaciones, una fila por contenedor. */
export function ImportacionesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const readOnly = useIsReadOnly();
  const { config: activeCountry } = useActiveCountry();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<FiltroImportaciones>(FILTRO_INICIAL);
  const [dialogo, setDialogo] = useState<{ importacion: Importacion | null } | null>(null);
  const [anular, setAnular] = useState<Importacion | null>(null);

  const q = useQuery({
    queryKey: ['importaciones', activeCountry.code],
    queryFn: listImportaciones,
    staleTime: 30_000,
    // Compras y pagos se cargan en otros módulos, que no conocen esta query.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 120_000,
  });

  // Cubre compras y pagos (el pago cambia el estado de la compra). La tabla
  // `importaciones` no está en Realtime: sus cambios llegan por el intervalo.
  useRealtimeInvalidate('purchase_headers', ['importaciones']);

  const filas = useMemo(() => {
    const b = busqueda.trim().toLowerCase();
    const desde = parseIso(filtro.desde);
    const hasta = parseIso(filtro.hasta);
    return (q.data ?? []).filter((i) => {
      if (!filtro.status.has(i.status) || !filtro.moneda.has(i.moneda)) return false;
      if (desde || hasta) {
        const fecha = parseIso(i.fecha);
        if (!fecha || !inRange(fecha, desde, hasta)) return false;
      }
      if (!b) return true;
      return [String(i.numero), i.descripcion, i.fabricante ?? '', i.status].some((v) => v.toLowerCase().includes(b));
    });
  }, [q.data, busqueda, filtro]);

  const pendiente = useMemo(
    () => ({
      ARS: filas.reduce((s, i) => s + (i.pendiente.ARS ?? 0), 0),
      USD: filas.reduce((s, i) => s + (i.pendiente.USD ?? 0), 0),
    }),
    [filas],
  );

  const filtroActivo =
    filtro.desde !== '' ||
    filtro.hasta !== '' ||
    !setsEqual(filtro.status, FILTRO_INICIAL.status) ||
    !setsEqual(filtro.moneda, FILTRO_INICIAL.moneda);

  const acciones = (i: Importacion) => {
    const abierta = i.status === 'Abierta';
    const conCompras = i.cantidadFacturas > 0;
    return (
      <div className="inline-flex items-center gap-1">
        <IconAction title="Ver detalle" onClick={() => navigate(`/importaciones/${i.id}`)}>
          <Eye className="h-4 w-4" aria-hidden />
        </IconAction>
        {!readOnly && (
          <>
            <IconAction
              title={abierta ? 'Editar' : 'Sólo en importaciones abiertas'}
              disabled={!abierta}
              onClick={() => setDialogo({ importacion: i })}
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </IconAction>
            <IconAction
              title={
                i.status === 'Anulada'
                  ? 'La importación ya está anulada'
                  : conCompras
                    ? 'Tiene compras asociadas: anulalas primero en Compras'
                    : 'Anular'
              }
              destructive
              disabled={i.status === 'Anulada' || conCompras}
              onClick={() => setAnular(i)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </IconAction>
          </>
        )}
      </div>
    );
  };

  const vacio =
    busqueda.trim() || filtroActivo ? 'Sin coincidencias para los filtros aplicados.' : 'Todavía no hay importaciones.';
  const errorCarga = (
    <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-destructive">
      <span>No se pudieron cargar las importaciones.</span>
      <Button variant="outline" size="sm" onClick={() => q.refetch()}>
        Reintentar
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-semibold tracking-tight text-foreground md:text-3xl">Importaciones</h1>
        {!readOnly && (
          <Button
            onClick={() => setDialogo({ importacion: null })}
            className="order-2 h-12 w-12 shrink-0 gap-2 bg-foreground p-0 text-background hover:bg-foreground/90 sm:order-none sm:w-auto sm:px-4"
            aria-label="Nueva importación"
            title="Nueva importación"
          >
            <span className="hidden sm:inline">Nueva importación</span>
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        )}
        <div className="order-3 basis-full sm:hidden" aria-hidden />
        <div className="relative order-4 min-w-[10rem] flex-1 sm:order-none sm:w-60 sm:flex-none">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar"
          />
        </div>
        <div className="order-5 sm:order-none">
          <FiltrarPopover
            value={filtro}
            onChange={setFiltro}
            onReset={() => setFiltro(FILTRO_INICIAL)}
            active={filtroActivo}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => qc.invalidateQueries({ queryKey: ['importaciones'] })}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} aria-hidden />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="order-1 h-12 w-12 shrink-0 sm:order-none"
          onClick={() => void exportarXlsx(filas)}
          disabled={filas.length === 0}
          aria-label="Descargar base en Excel"
          title="Descargar base en Excel"
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
        {q.isPending ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        ) : q.isError ? (
          errorCarga
        ) : filas.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground">{vacio}</p>
        ) : (
          <>
            <div className="hidden min-h-0 flex-1 overflow-auto md:block">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <Th>Estado</Th>
                    {/* Fecha de alta y Cant. FC sólo en pantallas anchas: con el menú abierto no entran. */}
                    <Th className="hidden 2xl:table-cell">Fecha</Th>
                    <Th>N°</Th>
                    <Th>Descripción</Th>
                    <Th>Fabricante</Th>
                    <Th>Fecha prob.</Th>
                    <Th>Etapa en curso</Th>
                    <Th className="hidden text-right 2xl:table-cell">Cant. FC</Th>
                    <Th className="text-right">Total FOB</Th>
                    <Th className="text-right">Total</Th>
                    <Th className="text-right">Pendiente</Th>
                    <Th className="text-right">Acciones</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filas.map((i) => (
                    <tr key={i.id} className="transition-colors hover:bg-muted/30">
                      <Td>
                        <EstadoImportacion status={i.status} />
                      </Td>
                      <Td className="hidden whitespace-nowrap text-muted-foreground 2xl:table-cell">{fechaImp(i.fecha)}</Td>
                      <Td className="tabular-nums">{i.numero}</Td>
                      <Td className="max-w-[200px] truncate font-medium text-foreground" title={i.descripcion}>
                        {i.descripcion}
                      </Td>
                      <Td className="max-w-[150px] truncate" title={i.fabricante ?? undefined}>{i.fabricante ?? '—'}</Td>
                      <Td className="whitespace-nowrap text-muted-foreground">{fechaImp(i.fechaProbable)}</Td>
                      <Td
                        className={cn(
                          'max-w-[160px] truncate',
                          i.etapaActual?.estado === 'vencida' ? 'font-medium text-rose-700' : 'text-muted-foreground',
                        )}
                        title={i.etapaActual?.estado === 'vencida' ? 'Etapa vencida' : undefined}
                      >
                        {etapaEnCurso(i) ?? '—'}
                      </Td>
                      <Td className="hidden text-right tabular-nums 2xl:table-cell">{i.cantidadFacturas}</Td>
                      <Td className="whitespace-nowrap text-right tabular-nums">{fob(i)}</Td>
                      <Td className="whitespace-nowrap text-right font-semibold tabular-nums">
                        {montosPorMoneda(i.total)}
                      </Td>
                      <Td className="whitespace-nowrap text-right font-semibold tabular-nums">
                        {montosPorMoneda(i.pendiente)}
                      </Td>
                      <Td className="whitespace-nowrap text-right">{acciones(i)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto md:hidden">
              {filas.map((i) => (
                <li key={i.id} className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      N° {i.numero} · {i.descripcion}
                    </span>
                    <EstadoImportacion status={i.status} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {i.fabricante ?? 'Sin fabricante'} · Llega {fechaImp(i.fechaProbable)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total FOB</div>
                      <div className="font-semibold text-foreground">{fob(i)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Pendiente</div>
                      <div className="font-semibold text-foreground">{montosPorMoneda(i.pendiente)}</div>
                    </div>
                  </div>
                  <div className="flex justify-end">{acciones(i)}</div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-2xl border bg-card px-3 py-3 sm:justify-end sm:px-4">
        <TotalPendiente etiqueta="Pendiente Pesos" monto={formatMoney(pendiente.ARS, 'ARS')} />
        <TotalPendiente etiqueta="Pendiente Dólares" monto={formatMoney(pendiente.USD, 'USD')} />
      </div>

      <ImportacionDialog
        open={dialogo != null}
        importacion={dialogo?.importacion ?? null}
        onClose={() => setDialogo(null)}
        onCreada={(id) => navigate(`/importaciones/${id}`)}
      />
      <AnularImportacionDialog importacion={anular} onClose={() => setAnular(null)} />
    </div>
  );
}

function TotalPendiente({ etiqueta, monto }: { etiqueta: string; monto: string }) {
  return (
    <span className="inline-flex h-9 items-center gap-2 rounded-lg border bg-secondary px-3 text-xs">
      <span className="text-muted-foreground">{etiqueta}</span>
      <span className="font-semibold text-foreground">{monto}</span>
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('whitespace-nowrap px-2.5 py-3 font-semibold', className)}>{children}</th>;
}

function Td({ children, className, title }: { children: React.ReactNode; className?: string; title?: string }) {
  return (
    <td className={cn('px-2.5 py-3', className)} title={title}>
      {children}
    </td>
  );
}
